import React from 'react';
import { MagnifyingGlass, MapPinLine, Package } from '@phosphor-icons/react';

import { ConsoleShell, MarketShell } from '../../../design-system/components/shells';
import { Empty, PageHeader } from '../../../design-system/components/desktop/primitives';
import { useViewport } from '../../../design-system/hooks/useViewport';
import { routes } from '../../../routes/routes';

import DisputeForm from './DisputeForm';
import ReviewFormView from './ReviewForm.view';
import {
  TrackActions,
  TrackButton,
  TrackCard,
  TrackDialog,
  TrackFact,
  TrackFacts,
  TrackHalted,
  TrackInput,
  TrackPanel,
  TrackPanels,
  TrackPromise,
  TrackStatusPill,
  TrackStepper,
} from './tracking.parts';
import { isHalted, labelFor, stepIndexFor } from './tracking.status';
import './tracking.css';

/**
 * The ONE order-tracking view. Two routes render it.
 *
 * WHY ONE FILE AND NOT TWO
 * ------------------------
 * `/order-tracking` and `/admin-order-tracking` were 119 and 107 lines of
 * line-for-line identical code. The mutation, the submit handler, the step
 * machine, the two modals and the whole body were the same characters twice;
 * the ONLY difference was `<Layout>` versus `<AdminLayout title="Order
 * Tracking">`, and a `"track-order"` versus `"track-order-admin"` mutation key.
 * Keeping them apart meant every tracking fix had to be written twice, and this
 * is the exact duplication `CONVERSION-MAP.md` sends B9 to remove.
 *
 * So the shape lives here once and the two containers supply a `variant`. The
 * table below is everything that actually differs between them, and it is five
 * lines. That is the same convergence B1 did to the two OTP screens; see
 * `components/OtpVerification/otp.view.jsx`.
 *
 * Pure, like `login.view.jsx`. No fetch, no navigate, no toast, no
 * localStorage, so the state explorer mounts the real screen rather than a mock
 * of it.
 *
 * THE STATUS VOCABULARY
 * ---------------------
 * Every status word here comes out of `utils/orderStatus.js` through
 * `tracking.status.js`. There is no "Shipped": it is not a value
 * `Shipment.status` can hold, whatever the older library templates show. The
 * four colour buckets, `warning`, `info`, `success` and `error`, plus `primary`
 * on `approved` alone, all map to real `Pill` tones.
 *
 * WHAT THE PAYLOAD CARRIES AND WHAT IS RENDERED
 * ---------------------------------------------
 * `orders/views.py::track_order` returns exactly
 * `{tracking_number, courier, status, last_update}`.
 *
 *   - `courier` is NOT rendered. The standing copy rule is that we never name
 *     one, and the timing is what the buyer actually wants to know. The old
 *     `ShipmentDetails` rendered it as "Carrier".
 *   - `origin`, if a future payload carries it, is `seller.store_name` and NOT
 *     a place, so it renders as "Seller". The old panel labelled it "Origin",
 *     which reads as a city of origin and is both wrong and against the rule.
 *   - `destination` is `order.city` and renders as "Delivering to", never as
 *     an origin.
 *   - No line items and no prices appear anywhere: the tracking payload has
 *     neither, and inventing them would be inventing data.
 *
 * Every fact row renders only when its value is present, so the four fields
 * today's endpoint omits are absent rather than four rows of "-".
 */

/** Everything that differs between the two routes. Nothing else does. */
export const TRACKING_VARIANTS = {
  /** `/order-tracking`, the public buyer lookup. MarketShell, per the map. */
  public: {
    shell: 'market',
    title: 'Order tracking',
    sub: 'Enter a tracking ID to see where a parcel is. Everything on Sleevr ships nationwide.',
    breadcrumbs: [
      { label: 'Marketplace', to: routes.MARKETPLACE },
      { label: 'Track order' },
    ],
  },
  /** `/admin-order-tracking`, the same lookup inside the admin console. */
  admin: {
    shell: 'console',
    title: 'Order tracking',
    sub: 'Look up any parcel by its tracking ID. Read only: nothing here changes a shipment.',
    breadcrumbs: [{ label: 'Admin' }, { label: 'Order tracking' }],
  },
};

/** The shell, chosen by variant. Both shells own their own track branching. */
function TrackingShell({ variant, children }) {
  const copy = TRACKING_VARIANTS[variant] || TRACKING_VARIANTS.public;

  if (copy.shell === 'console') {
    return (
      <ConsoleShell variant="admin" title={copy.title} breadcrumbs={copy.breadcrumbs} wide={false}>
        {children}
      </ConsoleShell>
    );
  }

  return (
    <MarketShell title={copy.title} breadcrumbs={copy.breadcrumbs} activeNav="orders">
      {children}
    </MarketShell>
  );
}

export function OrderTrackingView({
  variant = 'public',
  /* lookup */
  trackingId = '',
  onTrackingIdChange = () => {},
  onSubmit = () => {},
  loading = false,
  fieldError,
  /* `notFound` is the API's ONLY real failure mode: `track_order` 400s with
     "No shipment found for this order". It is not an error state, it is an
     empty result, so it gets an `Empty` and not a red line. */
  notFound = false,
  error,
  /* result */
  shipment = null,
  onReset,
  /* actions, gated by the container on buyer identity */
  canAct = false,
  /* review dialog */
  reviewOpen = false,
  onReviewOpen = () => {},
  onReviewClose = () => {},
  review = {},
  onReviewChange = () => {},
  onReviewSubmit = () => {},
  reviewLoading = false,
  reviewSubmitted = false,
  /* dispute dialog */
  disputeOpen = false,
  onDisputeOpen = () => {},
  onDisputeClose = () => {},
  dispute = {},
  onDisputeChange = () => {},
  onDisputeSubmit = () => {},
  disputeLoading = false,
  disputeSubmitted = false,
}) {
  const { isDesktop } = useViewport();
  const copy = TRACKING_VARIANTS[variant] || TRACKING_VARIANTS.public;

  // The payload may be the raw shipment or a wrapper carrying `tracking`.
  // Both shapes were already tolerated before the conversion; keep tolerating.
  const tracking = (shipment && (shipment.tracking || shipment)) || null;
  const status = tracking ? tracking.status : null;
  const halted = status ? isHalted(status) : false;

  function handleSubmit(event) {
    event.preventDefault();
    onSubmit(trackingId);
  }

  /* ------------------------------------------------------------- lookup */

  // The app dropped the lookup form only once the parcel was delivered. It is
  // dropped as soon as a parcel resolves instead, because a form still sitting
  // under its own answer invites the user to re-run the search they just ran.
  // "Track another" is the way back and it puts the caret in the field.
  const lookup = tracking ? null : (
    <TrackCard className="dsa-track__lookup">
      <h2 className="dsa-track__lookup-title">Check order status</h2>
      <p className="dsa-track__lookup-sub">
        Your tracking ID is on the order confirmation and on the order in your account.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {/* Inline, beside the field that caused it. The app toasted "Please
            enter a tracking ID" from the corner of the screen, which is the
            one place the user is not looking. */}
        <label className={isDesktop ? 'dsk-label' : 'ds-field__label'} htmlFor="trackingId">
          Tracking ID
        </label>
        <TrackInput
          id="trackingId"
          name="trackingId"
          value={trackingId}
          onChange={(event) => onTrackingIdChange(event.target.value)}
          placeholder="1234-567-91011"
          autoComplete="off"
          invalid={Boolean(fieldError)}
          disabled={loading}
          aria-describedby={fieldError ? 'trackingError' : undefined}
        />
        {fieldError ? (
          <span className={isDesktop ? 'dsk-error' : 'ds-field__hint'} id="trackingError" role="alert">
            {fieldError}
          </span>
        ) : null}

        <div className="dsa-track__lookup-actions">
          <TrackButton type="submit" loading={loading} disabled={loading}>
            {loading ? 'Looking it up' : 'Track order'}
          </TrackButton>
        </div>
      </form>

      {/* Everything that is not "no such ID": the request itself failed. Words
          plus a border, so the severity is never carried by colour alone. */}
      {error ? (
        <div className="dsa-track__halted" role="alert">
          <span>{error}</span>
        </div>
      ) : null}

      {notFound ? (
        <div className="dsa-track__notfound">
          <TrackingNotFound trackingId={trackingId} />
        </div>
      ) : null}

      <TrackPromise />
    </TrackCard>
  );

  /* ------------------------------------------------------------- result */

  const result = tracking ? (
    <>
      <TrackCard>
        <div className="dsa-track__head">
          <div>
            <h2 className="dsa-track__head-title">
              {tracking.tracking_number ? `Tracking ${tracking.tracking_number}` : 'Tracking'}
            </h2>
            <p className="dsa-track__head-meta">
              {halted ? 'This parcel is not on its way.' : 'Live shipment status.'}
            </p>
          </div>
          <div className="dsa-track__head-end">
            <TrackStatusPill status={status} />
            {onReset ? (
              <TrackButton variant="ghost" size="sm" onClick={onReset}>
                Track another
              </TrackButton>
            ) : null}
          </div>
        </div>

        {halted ? (
          <TrackHalted>
            This parcel is marked {labelFor(status).toLowerCase()}. It is not travelling towards a
            delivery, so there is no progress to show. Payment on the order is held until this is
            resolved.
          </TrackHalted>
        ) : (
          <TrackStepper current={stepIndexFor(status)} />
        )}
      </TrackCard>

      <TrackPanels>
        <TrackPanel icon={Package} title="Shipment booking">
          <TrackFacts>
            <TrackFact label="Tracking number" value={tracking.tracking_number} mono />
            <TrackFact label="Order" value={tracking.order_reference} mono />
            {/* `origin` is seller.store_name, not a place. */}
            <TrackFact label="Seller" value={tracking.origin} />
            <TrackFact label="Booked" value={tracking.booking_date} />
          </TrackFacts>
        </TrackPanel>

        <TrackPanel icon={MapPinLine} title="Track summary">
          <TrackFacts>
            <TrackFact label="Current status" value={<TrackStatusPill status={status} />} />
            <TrackFact label="Last update" value={tracking.last_update} />
            {/* `destination` is order.city and is where it is GOING. */}
            <TrackFact label="Delivering to" value={tracking.destination} />
            {/* The payload also carries `courier`. Deliberately not rendered:
                we never name one. Timing answers the real question. */}
            <TrackFact label="Expected" value={tracking.expected_delivery} />
          </TrackFacts>
        </TrackPanel>
      </TrackPanels>

      {canAct ? (
        <TrackCard>
          <TrackActions note="Only the buyer on this order sees these.">
            <TrackButton onClick={onReviewOpen} disabled={reviewSubmitted}>
              {reviewSubmitted ? 'Review submitted' : 'Leave a review'}
            </TrackButton>
            <TrackButton variant="ghost" onClick={onDisputeOpen} disabled={disputeSubmitted}>
              {disputeSubmitted ? 'Dispute opened' : 'Open a dispute'}
            </TrackButton>
          </TrackActions>
        </TrackCard>
      ) : null}
    </>
  ) : null;

  /* ------------------------------------------------------------- header */

  // Above the handoff the page needs its own title block; below it the shell's
  // top bar already carries one, so a second would be the same words twice.
  const header = isDesktop ? <PageHeader title={copy.title} sub={copy.sub} /> : null;

  return (
    <TrackingShell variant={variant}>
      {header}

      <div className="dsa-track">
        {lookup}
        {result}
      </div>

      <TrackDialog
        open={reviewOpen}
        onClose={onReviewClose}
        title="Leave a review"
        sub="Your review goes to the seller on this order."
      >
        <ReviewFormView
          orderId={shipment && shipment.orderId}
          rating={review.rating || 0}
          onRatingChange={(value) => onReviewChange({ ...review, rating: value })}
          review={review.review || ''}
          onReviewChange={(value) => onReviewChange({ ...review, review: value })}
          onSubmit={onReviewSubmit}
          onCancel={onReviewClose}
          loading={reviewLoading}
        />
      </TrackDialog>

      <TrackDialog
        open={disputeOpen}
        onClose={onDisputeClose}
        title="Open a dispute"
        sub="Payment on this order is held until the dispute is resolved."
      >
        <DisputeForm
          reason={dispute.reason || ''}
          onReasonChange={(value) => onDisputeChange({ ...dispute, reason: value })}
          description={dispute.description || ''}
          onDescriptionChange={(value) => onDisputeChange({ ...dispute, description: value })}
          evidencePreview={dispute.evidencePreview || null}
          onEvidenceChange={(event) => onDisputeChange({ ...dispute, evidenceEvent: event })}
          onSubmit={onDisputeSubmit}
          onSkip={onDisputeClose}
          loading={disputeLoading}
        />
      </TrackDialog>
    </TrackingShell>
  );
}

/**
 * The "no such tracking ID" result, which is what the API's only failure mode
 * actually is: `track_order` 400s with "No shipment found for this order".
 * Rendered by the view above through `error`, and exported here so the empty
 * shape stays in one place.
 */
export function TrackingNotFound({ trackingId, onReset }) {
  return (
    <Empty
      icon={MagnifyingGlass}
      illustration="not-found"
      title="No parcel with that tracking ID"
      text="Check the ID on your order confirmation and try again. It can take a few hours after checkout before a new parcel is trackable."
      query={trackingId}
      actions={
        onReset ? (
          <TrackButton variant="ghost" onClick={onReset}>
            Try another ID
          </TrackButton>
        ) : null
      }
    />
  );
}

export default OrderTrackingView;
